import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { VirtualTour } from '@app/core/model/entities/asset/virtual-tour';
import { Entity } from '@app/core/model/entities/entity';
import { Equipment } from '@app/core/model/entities/equipments/equipment';
import { Work } from '@app/core/model/entities/works/work';
import { EquipmentsService } from '@app/features/main/views/equipments/equipments.service';
import { VirtualToursService } from '@app/features/main/views/virtual-tours/virtual-tours.service';
import { WorksService } from '@app/features/main/views/works/works.service';
import { AbstractVirtualTourComponent } from '@app/shared/components/virtual-tour/abstract-virtual-tour.component';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { EMPTY, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'virtual-tour-mya',
    templateUrl: '../virtual-tour.component.html',
    styleUrls: ['../virtual-tour.component.scss'],
    providers: [VirtualToursService, WorksService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

/**
 * Component implementing all the logics behind the Metareal Virtual tour using the Metareal SDK.
 */
export class VirtualTourMyaComponent extends AbstractVirtualTourComponent implements OnInit {
  private readonly worksService = inject(WorksService);
  private readonly equipmentService = inject(EquipmentsService);

  // Container containing the Metareal Virtual tour
  @ViewChild('metareal')
  protected set container(el: ElementRef | undefined) {
    if (!el) {
      this._container = undefined;
      return;
    }
    this.onViewReady(el);
  };
  // Container containing the component itself
  @ViewChild('tour') public tour: ElementRef;

  @Input() public virtualTours: VirtualTour[];

  public ngOnInit(): void {
    super.ngOnInit();

    this.worksService.sidePanelToggle$.pipe(
      filter(entity => entity === null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.hidePanel());

    this.equipmentService.sidePanelToggle$.pipe(
      filter(entity => entity === null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.hidePanel());
  }

  /**
   * Fetch all entities related to the VirtualTour's Asset from MyA API
   */
  protected getEntities(): Observable<Entity[]> {
    const selectParams: SelectParams = {
      locale: getLocale(),
      filters: [
        eq('assetId', this.currentVirtualTour.assetId)
      ]
    };
    switch (this.currentVirtualTour.entity) {
      case EntityTypeEnum.WORK:
        return this.worksService.loadAllWorks(selectParams);
      case EntityTypeEnum.EQUIPMENT:
        return this.equipmentService.loadAllEquipments(selectParams);
      default:
        return EMPTY;
    }
  }

  protected setPanelContent(entityId: string): boolean {
    switch (this.currentVirtualTour.entity) {
      case EntityTypeEnum.WORK:
        const work = this.entities$.value.find(entity => entity.identifier === Number.parseInt(entityId)) as Work;
        work ? this.worksService.openWorkSidePanel(work) : alert(this.translate.instant('MESSAGE.NO_RELATED_ENTITY'));
        return !!work;
      case EntityTypeEnum.EQUIPMENT:
        const equipment = this.entities$.value.find(entity => entity.identifier === Number.parseInt(entityId)) as Equipment;
        equipment ? this.equipmentService.openEquipmentSidePanel(equipment) : alert(this.translate.instant(
          'MESSAGE.NO_RELATED_ENTITY'));
        return !!equipment;
      default:
        return false;
    }
  }

  /**
   * Return the appSheet entity ID
   * @param entity the appSheet entity
   */
  protected getEntityId(entity: Entity): string {
    return (entity['identifier'] as number).toString();
  }

  /**
   * Format appSheet entity for display purpose
   * @param entity an appSheet entity
   */
  public valueFormatter(entity: Entity): string {
    return entity?.toString();
  }
}
